import { getInstance } from "./instance";

const instance = getInstance();

export const getZoomRecordsByEmail = async (email: string, page: string, perPage: string, topic: string, startDate: string, endDate: string) => {
    const url = `zoom/screen-recording/get-records/${email}`;
    const params = { page, perPage, topic, startDate, endDate };
  
    return instance
      .get(url, { params })
      .then((res) => {
          return res.data;
      })
      .catch((error) => {
          return Promise.reject(error);
      });
  };
  
export const getPlayZoomUrl = async (id: string) => {
    const url = `zoom/screen-recording/play-video/${id}`;
  
    return instance
      .get(url)
      .then((res) => {
          return res.data;
      })
      .catch((error) => {
          return Promise.reject(error);
      });
  };

export const getZoomTeamMembersList = async () => {
    const url = "abo/screen-recording/get-coaches-list";

    return instance
      .get(url)
      .then((res) => {
          if (Array.isArray(res.data)) {
              return res.data.map(member => ({
                  email: member.hostEmail,
                  fullName: member.fullName,
                  meetingCount: member.meetingCount,
                  phoneCalls: member.phoneCalls
              }));
          } else {
              throw new Error('Data is not in expected format');
          }
      })
      .catch((error) => {
          throw error;
      });
};

export const getZoomPhoneMembersList = async (email: string) => {
    const url = "zoom/phone-call/zoom-user";
    const params = { email };
  
    return instance
      .get(url, { params })
      .then((res) => {
          return res.data;
      })
      .catch((error) => {
          return Promise.reject(error);
      });
  };

  export const getZoomCallRecordsByEmail = async (email: string, page: string, perPage: string, startDate: string, endDate: string, nextPageToken: string, searchNumber: string) => {
    const url = `zoom/phone-call/get-call-logs/${email}/${nextPageToken}`;
    const params = { page, perPage, startDate, endDate, phoneNumber: searchNumber };

    return instance
      .get(url, { params })
      .then((res) => {
          return res.data;
      })
      .catch((error) => {
          return Promise.reject(error);
      });
};


export const getPlayCallZoomUrl = async (id: string) => {
    const url = `zoom/phone-call/download-call/${id}`;
  
    return instance
      .get(url)
      .then((res) => {
          return res.data;
      })
      .catch((error) => {
          return Promise.reject(error);
      });
};

// TRANSCRIPT API FUNCTIONS
import { logger, LogCategory } from "../../services/logger";

/**
 * Get transcript metadata for a recording
 */
export const getTranscriptById = async (recordingId: string) => {
    const url = `zoom/screen-recording/transcript/${recordingId}`;
    
    
    logger.info(
        LogCategory.TRANSCRIPT_API,
        'GET_TRANSCRIPT_METADATA',
        `Fetching transcript metadata for recording ${recordingId}`,
        { recordingId, url }
    );
    
    const startTime = performance.now();
    
    try {
        const response = await instance.get(url);
        const duration = performance.now() - startTime;
        
        
        logger.info(
            LogCategory.TRANSCRIPT_API,
            'GET_TRANSCRIPT_METADATA_SUCCESS',
            `Successfully fetched transcript metadata in ${duration}ms`,
            {
                recordingId,
                duration,
                hasTranscript: response.data.hasTranscript,
                transcriptFormat: response.data.transcriptFormat,
                responseSize: JSON.stringify(response.data).length
            }
        );
        
        logger.metric(
            LogCategory.TRANSCRIPT_PERFORMANCE,
            'API_RESPONSE_TIME',
            { duration },
            { endpoint: url, method: 'GET' }
        );
        
        return response.data;
    } catch (error: any) {
        const duration = performance.now() - startTime;
        
        
        logger.error(
            LogCategory.TRANSCRIPT_API,
            'GET_TRANSCRIPT_METADATA_ERROR',
            `Failed to fetch transcript metadata after ${duration}ms`,
            error,
            {
                recordingId,
                url,
                duration,
                errorCode: error.response?.status,
                errorMessage: error.response?.data?.message
            }
        );
        
        return null;
    }
};

/**
 * Get signed URL for transcript download
 */
export const getTranscriptUrl = async (recordingId: string) => {
    const url = `zoom/screen-recording/transcript-url/${recordingId}`;
    
    logger.info(
        LogCategory.TRANSCRIPT_API,
        'GET_TRANSCRIPT_URL',
        `Requesting signed URL for transcript ${recordingId}`,
        { recordingId, url }
    );
    
    return logger.measurePerformance(
        LogCategory.TRANSCRIPT_API,
        'GET_TRANSCRIPT_URL_PERFORMANCE',
        async () => {
            try {
                const response = await instance.get(url);
                
                logger.info(
                    LogCategory.TRANSCRIPT_API,
                    'GET_TRANSCRIPT_URL_SUCCESS',
                    'Successfully obtained signed URL',
                    {
                        recordingId,
                        expiresIn: response.data.expiresIn,
                        contentType: response.data.contentType,
                        urlLength: response.data.signedUrl?.length
                    }
                );
                
                return response.data;
            } catch (error: any) {
                logger.error(
                    LogCategory.TRANSCRIPT_API,
                    'GET_TRANSCRIPT_URL_ERROR',
                    'Failed to get transcript URL',
                    error,
                    { recordingId, url }
                );
                throw error;
            }
        },
        { recordingId }
    );
};

/**
 * Get transcript content directly from backend (avoids CORS issues)
 */
export const getTranscriptContent = async (recordingId: string) => {
    const url = `zoom/screen-recording/transcript-content/${recordingId}`;
    
    
    logger.info(
        LogCategory.TRANSCRIPT_API,
        'GET_TRANSCRIPT_CONTENT',
        `Fetching transcript content for recording ${recordingId}`,
        { recordingId, url }
    );
    
    const startTime = performance.now();
    
    try {
        const response = await instance.get(url);
        const duration = performance.now() - startTime;
        
        
        logger.info(
            LogCategory.TRANSCRIPT_API,
            'GET_TRANSCRIPT_CONTENT_SUCCESS',
            `Successfully fetched transcript content in ${duration}ms`,
            {
                recordingId,
                duration,
                contentLength: response.data?.length
            }
        );
        
        return response.data;
    } catch (error: any) {
        const duration = performance.now() - startTime;
        
        
        logger.error(
            LogCategory.TRANSCRIPT_API,
            'GET_TRANSCRIPT_CONTENT_ERROR',
            `Failed to fetch transcript content after ${duration}ms`,
            error,
            {
                recordingId,
                url,
                duration,
                errorCode: error.response?.status,
                errorMessage: error.response?.data?.message
            }
        );
        
        throw error;
    }
};

/**
 * Check if transcript is available for a recording
 */
export const checkTranscriptAvailability = async (recordingId: string): Promise<boolean> => {
    try {
        const transcript = await getTranscriptById(recordingId);
        return transcript?.hasTranscript === true;
    } catch (error) {
        return false;
    }
};

/**
 * Get list of recordings with transcript filter and search capabilities
 */
export const getRecordingsWithTranscripts = async (
    email: string,
    page: number = 0,
    perPage: number = 15,
    hasTranscript?: boolean,
    topic?: string,
    startDate?: string,
    endDate?: string
) => {
    const url = `zoom/screen-recording/with-transcripts/${email}`;
    const params: any = { page, perPage };
    
    // Add filters if provided
    if (hasTranscript !== undefined) params.hasTranscript = hasTranscript;
    if (topic) params.topic = topic;
    if (startDate) params.dateStart = startDate;
    if (endDate) params.dateEnd = endDate;
    
    logger.info(
        LogCategory.TRANSCRIPT_API,
        'GET_RECORDINGS_WITH_TRANSCRIPTS',
        `Fetching recordings with transcripts for ${email}`,
        { email, page, perPage, hasTranscript, topic, startDate, endDate }
    );
    
    return instance
        .get(url, { params })
        .then((res) => {
            logger.info(
                LogCategory.TRANSCRIPT_API,
                'GET_RECORDINGS_WITH_TRANSCRIPTS_SUCCESS',
                `Successfully fetched recordings`,
                {
                    email,
                    totalRecords: res.data.meta?.total,
                    recordsReturned: res.data.data?.length,
                    filters: { hasTranscript, topic, startDate, endDate }
                }
            );
            return res.data;
        })
        .catch((error) => {
            logger.error(
                LogCategory.TRANSCRIPT_API,
                'GET_RECORDINGS_WITH_TRANSCRIPTS_ERROR',
                'Failed to fetch recordings with transcripts',
                error,
                { email, page, perPage, hasTranscript, topic, startDate, endDate }
            );
            return Promise.reject(error);
        });
};
